import { useState, type FormEvent } from "react";
import { useActiveTenant } from "../tenant/TenantContext";
import { useTenantMutation, useTenantQuery } from "../hooks/useTenantData";
import { can } from "../tenant/permissions";
import {
  ROLE_LABELS,
  type AdminUser,
  type AdminUserInput,
  type TenantRole,
} from "../api/types";
import {
  Badge,
  ConfirmDialog,
  Field,
  InlineError,
  PageHeader,
  Panel,
  QueryState,
} from "../components/ui";

const ASSIGNABLE_ROLES: TenantRole[] = [
  "tenant_owner",
  "tenant_admin",
  "building_manager",
  "concierge_operator",
  "auditor",
];

export function AdminUsersPage() {
  const membership = useActiveTenant();
  const canManage = can(membership.role, "admins.manage");
  const canManageOwner = can(membership.role, "admins.manage_owner");
  const users = useTenantQuery(["admin-users"], (api, t) => api.listAdminUsers(t));

  const invite = useTenantMutation<AdminUserInput, AdminUser>(
    (api, t, input) => api.inviteAdminUser(t, input),
    [["admin-users"], ["audit-events"]],
  );
  const changeRole = useTenantMutation<{ id: string; role: TenantRole }>(
    (api, t, { id, role }) => api.updateAdminUser(t, id, { role }),
    [["admin-users"], ["audit-events"]],
  );
  const revoke = useTenantMutation<string>(
    (api, t, id) => api.removeAdminUser(t, id),
    [["admin-users"], ["audit-events"]],
  );
  const [revoking, setRevoking] = useState<AdminUser | null>(null);

  function submitInvite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    invite.mutate(
      {
        name: String(data.get("name") || ""),
        email: String(data.get("email") || ""),
        role: data.get("role") as TenantRole,
      },
      { onSuccess: () => form.reset() },
    );
  }

  return (
    <>
      <PageHeader
        title="Usuários administrativos"
        subtitle="Quem pode acessar este portal e com qual papel."
      />
      {canManage && (
        <Panel title="Convidar novo usuário">
          <form onSubmit={submitInvite} className="form-grid">
            <Field label="Nome" name="name" required />
            <Field label="E-mail" name="email" type="email" required />
            <label>
              Papel
              <select name="role" defaultValue="building_manager">
                {ASSIGNABLE_ROLES.filter((r) => r !== "tenant_owner" || canManageOwner).map((r) => (
                  <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                ))}
              </select>
            </label>
            <div className="form-actions">
              <button type="submit" disabled={invite.isPending}>
                {invite.isPending ? "Enviando…" : "Convidar"}
              </button>
            </div>
          </form>
          <p>O acesso usa a conta da própria pessoa no Microsoft Entra — o portal não cria senhas. Nesta POC, compartilhe o link por um canal aprovado.</p>
          {invite.data?.activation_url && (
            <div className="notice">
              <strong>Convite criado</strong>
              <p>Compartilhe este link pelo canal aprovado. Ele é exibido somente agora.</p>
              <p className="invite-link">{invite.data.activation_url}</p>
              {invite.data.pin && <p>PIN: <strong>{invite.data.pin}</strong></p>}
              <button
                type="button"
                className="secondary"
                onClick={() => void navigator.clipboard.writeText(invite.data!.activation_url!)}
              >
                Copiar link
              </button>
            </div>
          )}
          <InlineError error={invite.error} />
        </Panel>
      )}
      <Panel title="Usuários do condomínio">
        <QueryState query={users} empty="Nenhum usuário administrativo cadastrado.">
          {(items) => {
            const activeOwners = items.filter((u) => u.role === "tenant_owner" && u.status === "active");
            return (
              <div className="table">
                {items.map((user) => {
                  const isLastOwner = user.role === "tenant_owner" && user.status === "active" && activeOwners.length <= 1;
                  const canTouch =
                    canManage && (user.role !== "tenant_owner" || canManageOwner) && user.status !== "revoked";
                  return (
                    <div className="table-row" key={user.id}>
                      <span>
                        <strong>{user.name}</strong>
                        <small>{user.email}</small>
                      </span>
                      <span>{ROLE_LABELS[user.role]}</span>
                      <StatusBadge status={user.status} />
                      <span className="row-actions">
                        {canTouch && (
                          <>
                            <select
                              aria-label={`Alterar papel de ${user.name}`}
                                value={user.role}
                                disabled={isLastOwner || changeRole.isPending}
                                onChange={(event) =>
                                  changeRole.mutate({ id: user.id, role: event.target.value as TenantRole })
                                }
                              >
                                {ASSIGNABLE_ROLES.filter((r) => r !== "tenant_owner" || canManageOwner).map((r) => (
                                  <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                                ))}
                              </select>
                            <button
                              type="button"
                              className="danger"
                              disabled={isLastOwner}
                              title={isLastOwner ? "O condomínio precisa manter pelo menos um responsável ativo." : undefined}
                              onClick={() => setRevoking(user)}
                            >
                              Revogar
                            </button>
                          </>
                        )}
                        {isLastOwner && <Badge tone="warn">Último responsável ativo</Badge>}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          }}
        </QueryState>
        <InlineError error={changeRole.error} />
        <InlineError error={revoke.error} />
      </Panel>
      <ConfirmDialog
        open={revoking !== null}
        title="Revogar acesso"
        description={
          revoking
            ? `Remover o acesso de ${revoking.name} (${ROLE_LABELS[revoking.role]}) a este condomínio? A pessoa deixará de acessar o portal imediatamente.`
            : ""
        }
        confirmLabel="Revogar acesso"
        busy={revoke.isPending}
        onCancel={() => setRevoking(null)}
        onConfirm={() => {
          if (revoking) revoke.mutate(revoking.id, { onSettled: () => setRevoking(null) });
        }}
      />
    </>
  );
}

function StatusBadge({ status }: { status: AdminUser["status"] }) {
  if (status === "active") return <Badge tone="ok">Ativo</Badge>;
  if (status === "invited") return <Badge tone="warn">Convite pendente</Badge>;
  return <Badge tone="muted">Revogado</Badge>;
}
